import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Building2, Users, Briefcase, BarChart3, LogOut, Menu, X, AlertCircle, Home, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { getUser, clearAuth, setAuth } from '@/utils/auth';
import { toast } from 'sonner';
import api from '@/utils/api';
import EmployerDashboardHome from './EmployerDashboardHome';
import CompanyProfile from './CompanyProfile';
import HRManagement from './HRManagement';
import JobManagement from './JobManagement';
import ApplicationManagement from './ApplicationManagement';
import EmployerTermsModal from '@/components/EmployerTermsModal';

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [isAdminViewing, setIsAdminViewing] = useState(false);

  // Check for admin session from URL parameter (for "Login as Employer" feature)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const adminSession = params.get('admin_session');
    
    if (adminSession) {
      try {
        const sessionData = JSON.parse(decodeURIComponent(adminSession));
        if (sessionData.token && sessionData.user) {
          // Set the employer session
          setAuth(sessionData.token, sessionData.user);
          setIsAdminViewing(sessionData.user.is_admin_impersonating || false);
          // Clean the URL
          window.history.replaceState({}, document.title, '/employer/dashboard');
        }
      } catch (error) {
        console.error('Failed to parse admin session:', error);
      }
    }
  }, []);

  const user = getUser();
  
  // Check if user is HR (not main employer)
  const isHR = user?.role === 'hr';

  // Check if admin is viewing this dashboard
  useEffect(() => {
    if (user?.is_admin_impersonating) {
      setIsAdminViewing(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user || (user.role !== 'employer' && user.role !== 'hr')) {
      toast.error('Unauthorized access');
      navigate('/employer/login');
      return;
    }
    // Check if HR user is inactive
    if (user?.role === 'hr' && user?.active === false) {
      toast.error('Your account has been deactivated. Please contact your employer.');
      clearAuth();
      navigate('/employer/login');
      return;
    }
    // Fetch company name
    fetchCompanyName();
    // Check terms acceptance
    checkTermsAcceptance();
  }, [user, navigate]);

  const checkTermsAcceptance = async () => {
    try {
      const response = await api.get(`/auth/user/${user.id}`);
      if (response.data) {
        if (!response.data.terms_accepted) {
          setShowTermsModal(true);
        } else {
          setTermsChecked(true);
        }
      }
    } catch (error) {
      console.error('Error checking terms:', error);
      // If user doesn't have terms_accepted field, show modal
      if (!user.terms_accepted) {
        setShowTermsModal(true);
      } else {
        setTermsChecked(true);
      }
    }
  };

  const handleTermsAccepted = () => {
    setShowTermsModal(false);
    setTermsChecked(true);
    // Refresh user data
    const updatedUser = { ...user, terms_accepted: true };
    setAuth(localStorage.getItem('auth_token'), updatedUser);
  };

  const fetchCompanyName = async () => {
    try {
      const employerId = isHR ? user?.employer_id : user?.id;
      if (employerId) {
        const response = await api.get(`/employer/company/${employerId}`);
        if (response.data) {
          setCompanyName(response.data.company_name || '');
        }
      }
    } catch (error) {
      console.error('Error fetching company name:', error);
    }
  };

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/employer/login');
  };

  // Menu items - filtered based on role
  const allMenuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/employer/dashboard', hrAccess: true },
    { icon: Building2, label: 'Company Profile', path: '/employer/profile', hrAccess: false },
    { icon: Users, label: 'HR Executives', path: '/employer/hr', hrAccess: false },
    { icon: Briefcase, label: 'Manage Jobs', path: '/employer/jobs', hrAccess: true },
    { icon: ClipboardList, label: 'Applications', path: '/employer/applications', hrAccess: true },
  ];

  // Filter menu items based on role
  const menuItems = isHR 
    ? allMenuItems.filter(item => item.hrAccess)
    : allMenuItems;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  if (!user) return null;

  // Show terms modal and block dashboard access until accepted
  if (showTermsModal) {
    return <EmployerTermsModal open={showTermsModal} onAccept={handleTermsAccepted} />;
  }

  // Wait for terms check to complete
  if (!termsChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div className={`p-4 border-b border-slate-700 ${isMobile ? '' : ''}`}>
        <div className="flex items-center justify-between">
          {(sidebarOpen || isMobile) && (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-10 w-auto object-contain bg-white rounded p-0.5" />
            </div>
          )}
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-slate-800"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          )}
        </div>
        {/* Company Name */}
        {(sidebarOpen || isMobile) && companyName && (
          <div className="mt-3 px-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Company</p>
            <p className="text-sm font-medium text-teal-400 truncate">{companyName}</p>
          </div>
        )}
      </div>

      {/* Role Badge */}
      {(sidebarOpen || isMobile) && (
        <div className="px-4 py-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            isHR ? 'bg-blue-600 text-white' : 'bg-teal-600 text-white'
          }`}>
            {isHR ? 'HR Executive' : 'Main Employer'}
          </span>
        </div>
      )}

      <nav className="flex-1 p-4 space-y-2">
        {/* Home Link */}
        <Link
          to="/"
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          onClick={() => isMobile && setMobileSidebarOpen(false)}
        >
          <Home className="w-5 h-5" />
          {(sidebarOpen || isMobile) && <span>Home</span>}
        </Link>
        
        <div className="border-t border-slate-700 my-2"></div>
        
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => isMobile && setMobileSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              isActive(item.path) 
                ? 'bg-teal-600 text-white' 
                : 'hover:bg-slate-800 text-slate-300'
            }`}
          >
            <item.icon className="w-5 h-5" />
            {(sidebarOpen || isMobile) && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-700">
        {(sidebarOpen || isMobile) && (
          <div className="mb-3">
            <p className="text-sm text-slate-400">Logged in as</p>
            <p className="font-medium text-white">{user.name || user.mobile}</p>
            {isHR && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Limited access mode
              </p>
            )}
          </div>
        )}
        <Button
          data-testid="employer-logout-btn"
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-white hover:bg-slate-800"
        >
          <LogOut className="w-5 h-5 mr-2" />
          {(sidebarOpen || isMobile) && 'Logout'}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Admin Viewing Indicator Banner */}
      {isAdminViewing && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-purple-600 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
          <span className="flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Admin View Mode: You are viewing {companyName || 'this employer'}'s dashboard. Changes will affect their account.
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-4 bg-white text-purple-600 hover:bg-purple-50 border-white h-7"
              onClick={() => window.close()}
            >
              Close Tab
            </Button>
          </span>
        </div>
      )}
      
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex ${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex-col ${isAdminViewing ? 'mt-10' : ''}`}>
        <SidebarContent />
      </aside>

      {/* Mobile Header & Sidebar */}
      <div className={`md:hidden fixed ${isAdminViewing ? 'top-10' : 'top-0'} left-0 right-0 z-50 bg-slate-900 text-white`}>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-10 w-auto object-contain bg-white rounded p-0.5" />
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isHR ? 'bg-blue-600' : 'bg-teal-600'
            }`}>
              {isHR ? 'HR' : 'Employer'}
            </span>
            <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-slate-900 text-white border-slate-700">
                <div className="flex flex-col h-full">
                  <SidebarContent isMobile={true} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <main className={`flex-1 overflow-y-auto md:mt-0 ${isAdminViewing ? 'mt-26' : 'mt-16'}`}>
        <div className="p-4 md:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/employer/dashboard" replace />} />
            <Route path="/dashboard" element={<EmployerDashboardHome />} />
            <Route path="/profile" element={isHR ? <Navigate to="/employer/dashboard" replace /> : <CompanyProfile />} />
            <Route path="/hr" element={isHR ? <Navigate to="/employer/dashboard" replace /> : <HRManagement />} />
            <Route path="/jobs" element={<JobManagement />} />
            <Route path="/applications" element={<ApplicationManagement />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default EmployerDashboard;
